import {
  ColumnFactory,
  createDataQualityCellFormatter,
  createIconCellFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DataQualityAssetType,
  DataQualityCellFormatterRowDataFunctions,
  DataQualityIconHeaderCertificationFormatterFactory,
  DataQualityIconHeaderFormatterFactory,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  mapDC3ColumnFactoriesToVizPortalColumnFactories,
  reportGraphQLErrors,
  TableMediumIcon,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { Formatters } from '@tableau/widgets/lib/gen-src/Localize';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { DatabaseTableOrderField } from '../../../gen-src/queries/globalTypes';
import {
  getDatabaseDisplayString,
  tableDatabaseCellTitle,
  tableDatabaseGroupIcon,
} from '../datagrid/cellFormatters/DatabaseNameCell';
import {
  createObfuscatedLinkCellFormatter,
  createObfuscatedNamesWithCountCellFormatter,
  createObfuscatedTextWithIconCellFormatter,
} from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
  makeDC3ColumnFactoryParamsFromVizPortalParams,
} from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';
import { CellFormatter } from './types/SharedDisplayGridTypes';

const shouldBeObfuscated: (rowData: TablesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type TablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  TablesDisplayGridRowData,
  TablesValidSortFields
>;

function iconColumn(): JSX.Element {
  return <TableMediumIcon isCertified={false} />;
}

const rowDataFunctions: DataQualityCellFormatterRowDataFunctions<
  TablesDisplayGridRowData
> = {
  getAssetUrlFromRowData: (urls: SiteUrls, r: TablesDisplayGridRowData) =>
    urls.catalogTableUrl(r.id),
  getNameFromRowData: (r: TablesDisplayGridRowData) => (r.name ? r.name : ''),
  getTypeNameFromRowData: (): DataQualityAssetType => 'DatabaseTable',
  getDataQualityCertificationFromRowData: (r: TablesDisplayGridRowData) =>
    r.__typename === 'DatabaseTable' ? r.dataQualityCertifications : [],
};

export const columns: ReadonlyArray<TablesColumnFactory> = [
  (sortParams) => ({
    name: 'type',
    dataKey: DatabaseTableOrderField.IS_CERTIFIED,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      () => iconColumn(),
      (rowData) =>
        rowData.__typename === 'DatabaseTable' && rowData.isCertified
          ? ''
          : Messages.Table()
    ),
    resizable: false,
    sortable: true,
  }),
  () => ({
    name: 'certification',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.Certification(),
    headerFormatter: DataQualityIconHeaderCertificationFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r: TablesDisplayGridRowData) =>
        r.__typename === 'DatabaseTable' ? r.dataQualityWarnings : [],
      disableIconOnlyTabIndex: true,
      isCertification: true,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  () => ({
    name: 'warning',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.DataQualityWarningTitle(),
    headerFormatter: DataQualityIconHeaderFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r) => {
        if (r.__typename !== 'DatabaseTable' || r.id === '') {
          return [];
        } else {
          return r.dataQualityWarnings;
        }
      },
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  (params) => ({
    name: 'name',
    dataKey: DatabaseTableOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(params),
    cellFormatter: createObfuscatedLinkCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name,
      (urls, { id }) => urls.catalogTablePath(id),
      undefined, // noPadding
      true // disableTabIndex
    ),
    growToFit: true,
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'virtualConnections',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_VIRTUAL_CONNECTIONS_COUNT,
    headerText: Messages.GridHeaderVirtualConnection(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamVirtualConnectionsConnection }) =>
        downstreamVirtualConnectionsConnection
          ? Formatters.formatCount(
              downstreamVirtualConnectionsConnection.totalCount
            )
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'workbooks',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
    headerText: Messages.GridHeaderWorkbooks(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'datasources',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
    headerText: Messages.GridHeaderDatasources(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'database',
    dataKey: '',
    headerText: Messages.GridHeaderDatabaseNamePath(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createObfuscatedTextWithIconCellFormatter<
      TablesDisplayGridRowData
    >(
      shouldBeObfuscated,
      getDatabaseDisplayString,
      tableDatabaseGroupIcon,
      tableDatabaseCellTitle,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: false,
  }),
  (sortParams) => ({
    name: 'columns',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderColumns(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createObfuscatedNamesWithCountCellFormatter(
      shouldBeObfuscated,
      ({ columnsConnection }) =>
        columnsConnection
          ? columnsConnection.nodes.map((column) => column.name || '')
          : [],
      ({ columnsConnection }) =>
        columnsConnection ? columnsConnection.totalCount : 0,
      undefined, // maxShown
      undefined, // ObfuscatedComponent
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: false,
  }),
  (sortParams) => ({
    name: 'contact',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderContact(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createUserNameLinkCellFormatter(
      (rowData) =>
        rowData.__typename === 'DatabaseTable' ? rowData.contact : null,
      true,
      (rowData) => rowData.id === ''
    ),
    resizable: true,
    sortable: false,
  }),
];

/**
 * @deprecated These cell formatters are already in use in the columns above. These defs below will be removed once we have
 * the columns used properly in vpc.
 */
const cellFormattersForCrass: Record<string, CellFormatter> = {
  dataQualityWarning: {
    type: 'table',
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: (r) => {
        if (r.__typename !== 'DatabaseTable' || r.id === '') {
          return [];
        } else {
          return r.dataQualityWarnings;
        }
      },
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
  },
  typeForCrass: {
    type: 'table',
    cellFormatter: createIconCellFormatter(
      () => iconColumn(),
      (rowData) =>
        rowData.__typename === 'DatabaseTable' && rowData.isCertified
          ? ''
          : Messages.Table()
    ),
  },
  name: {
    type: 'table',
    cellFormatter: createObfuscatedLinkCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name,
      (urls, { id }) => urls.catalogTablePath(id),
      undefined, // noPadding
      true, // disableTabIndex
      undefined, // ObfuscatedComponent
      (rowData) => rowData.id === ''
    ),
  },
  downstreamWorkbookCount: {
    type: 'table',
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
  },
  downstreamDataSourcesCount: {
    type: 'table',
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
  },
  database: {
    type: 'table',
    cellFormatter: createObfuscatedTextWithIconCellFormatter<
      TablesDisplayGridRowData
    >(
      shouldBeObfuscated,
      getDatabaseDisplayString,
      tableDatabaseGroupIcon,
      tableDatabaseCellTitle,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  columns: {
    type: 'table',
    cellFormatter: createObfuscatedNamesWithCountCellFormatter(
      shouldBeObfuscated,
      ({ columnsConnection }) =>
        columnsConnection
          ? columnsConnection.nodes.map((column) => column.name || '')
          : [],
      ({ columnsConnection }) =>
        columnsConnection ? columnsConnection.totalCount : 0,
      undefined, // maxShown
      undefined, // ObfuscatedComponent
      (rowData) => rowData.id === ''
    ),
  },
  contact: {
    type: 'table',
    cellFormatter: createUserNameLinkCellFormatter(
      (rowData) =>
        rowData.__typename === 'DatabaseTable' ? rowData.contact : null,
      true,
      (rowData) => rowData.id === ''
    ),
  },
};

export const columnsForVizPortal = mapDC3ColumnFactoriesToVizPortalColumnFactories(
  columns,
  makeDC3ColumnFactoryParamsFromVizPortalParams
);

export const tablesWithCrassCellFormatters = cellFormattersForCrass;

export type TablesDisplayGridProps = UsualDisplayGridProps<
  TablesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<TablesValidSortFields>;

export function TablesDisplayGrid(props: TablesDisplayGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const cols = columns
    .map((columnFactory) => columnFactory(props))
    .filter(column => {
      if (column.name === 'virtualConnections' && !publishedConnectionsLineageEnabled) {
        return false;
      }
      return true;
    });
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
